import { create } from 'zustand'
import { v4 as generate_uuid } from 'uuid'

export type TimeTravelEvent = {
  uuid: string
  timestamp: number
  source: 'view_data' | 'template_attribute' | 'document_page'
  action:
    | 'create'
    | 'update'
    | 'delete'
    | 'bulk_create'
    | 'bulk_delete'
    | 'bulk_update'
  old: any
  new: any
}

interface TimeTravelState {
  pastEvents: TimeTravelEvent[]
  futureEvents: TimeTravelEvent[]
  addEvent: (event: Omit<TimeTravelEvent, 'uuid' | 'timestamp'>) => void
  getState: () => TimeTravelState
  setState: (state: Partial<TimeTravelState>) => void
  undo: (overrides?: Partial<TimeTravelEvent>) => void
  redo: (overrides?: Partial<TimeTravelEvent>) => void
  reset: () => void
}

export const useTimeTravelStore = create<TimeTravelState>((set, get) => ({
  pastEvents: [],
  futureEvents: [],
  getState: () => get(),
  setState: (state: Partial<TimeTravelState>) => {
    set(state)
  },
  addEvent: (event: Omit<TimeTravelEvent, 'uuid' | 'timestamp'>) => {
    set(({ pastEvents }) => {
      const uuid = generate_uuid()
      const newEvent = {
        ...event,
        uuid,
        timestamp: Date.now(),
      }
      return {
        pastEvents: [...pastEvents, newEvent],
        futureEvents: [],
      }
    })
  },
  undo: (overrides?: Partial<TimeTravelEvent>) =>
    set(({ pastEvents, futureEvents }) => {
      if (pastEvents.length === 0) {
        return { pastEvents, futureEvents }
      }
      const unDoneEvent = pastEvents[pastEvents.length - 1]
      return {
        pastEvents: pastEvents.slice(0, pastEvents.length - 1),
        futureEvents: [
          ...futureEvents,
          { ...unDoneEvent, ...(overrides || {}) },
        ],
      }
    }),
  redo: (overrides?: Partial<TimeTravelEvent>) =>
    set(({ pastEvents, futureEvents }) => {
      if (futureEvents.length === 0) {
        return { pastEvents, futureEvents }
      }
      const reDoneEvent = futureEvents[futureEvents.length - 1]
      return {
        pastEvents: [...pastEvents, { ...reDoneEvent, ...(overrides || {}) }],
        futureEvents: futureEvents.slice(0, futureEvents.length - 1),
      }
    }),
  reset: () =>
    set({
      pastEvents: [],
      futureEvents: [],
    }),
}))
